import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SettingsModal from '../../components/SettingsModal';
import Alert from '../../components/Alert/Alert';
import { articleReadingConfig } from '../../config/settingsConfig';
import TRecorder from 'recorder-core';
import 'recorder-core/src/engine/mp3';
import 'recorder-core/src/engine/mp3-engine';
import { voiceAPI } from '../../services/api';
import listenIcon from '../../static/image/听.png';
import speakIcon from '../../static/image/说.png';
import logoImage from '../../static/image/白字logo.png';
import scoreGoodImage from '../../static/image/测评好.png';
import scoreBadImage from '../../static/image/测评差.png';
import scoringImage from '../../static/image/测评中.png';
import './RolePlay.css';
import { formatSentenceText } from '../../utils/formatText';

const RolePlay = () => {
  const navigate = useNavigate();
  const [selectedDialogues, setSelectedDialogues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // 角色选择状态
  const [isRoleSelectionMode, setIsRoleSelectionMode] = useState(true); // 是否在角色选择模式
  const [currentDialogueIndex, setCurrentDialogueIndex] = useState(0);
  const [selectedRoles, setSelectedRoles] = useState([]); // 选中的角色ID数组
  const [availableRoles, setAvailableRoles] = useState([]); // 当前对话可用的角色列表
  
  // 对话分页状态
  const [dialogues, setDialogues] = useState([]);
  
  // 句子导航状态
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const [maxVisitedSentenceIndex, setMaxVisitedSentenceIndex] = useState(0); // 记录最大已访问的句子索引
  
  // 显示控制
  const [showTranslation, setShowTranslation] = useState(true);
  
  // 播放状态管理
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const [isPlayingFullDialogue, setIsPlayingFullDialogue] = useState(false); // 新增：区分播放全文
  const isAutoPlayingRef = useRef(false);
  const isPlayingRef = useRef(false);
  const isPlayingFullDialogueRef = useRef(false); // 新增：区分播放全文的ref
  const playbackIdRef = useRef(0);
  const navigationTimeoutRef = useRef(null);
  const prevDialogueIndexRef = useRef(0);
  const playSentenceAudioRef = useRef(null);
  
  // 全局音频管理
  const [currentAudio, setCurrentAudio] = useState(null);
  const currentAudioRef = useRef(null);
  const [audioPlaybackCancelled, setAudioPlaybackCancelled] = useState(false);
  
  // 录音相关状态
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const recorderRef = useRef(null); // 改用TRecorder
  const recordingTimerRef = useRef(null);
  const screenClickHandlerRef = useRef(null); // 屏幕点击处理器
  const [waitingForUserSpeak, setWaitingForUserSpeak] = useState(false);
  
  // 存储每个句子的录音数据：{ sentenceIndex: { blob: Blob, url: string } }
  const [sentenceRecordings, setSentenceRecordings] = useState({});
  const sentenceRecordingsRef = useRef({});
  
  // 语音测评相关状态
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [evaluationResult, setEvaluationResult] = useState(null);
  const [showScoreImage, setShowScoreImage] = useState(false);
  const [scoreImageType, setScoreImageType] = useState('');
  const [recordedAudioUrl, setRecordedAudioUrl] = useState(null); // 保存录音的 URL
  const recordedAudioUrlRef = useRef(null); // 使用 ref 保存 URL，确保能正确清理
  
  // 设置弹窗状态
  const [showReadingSettings, setShowReadingSettings] = useState(false);
  const [readingSettings, setReadingSettings] = useState(articleReadingConfig.defaultSettings);
  const readingSettingsRef = useRef(articleReadingConfig.defaultSettings);
  
  // Alert 提示框状态
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  
  // 设置页面特殊标识（仅在组件挂载/卸载时执行）
  useEffect(() => {
    document.body.classList.add('role-play-page');
    
    return () => {
      document.body.classList.remove('role-play-page');
      
      // 组件卸载时停止音频播放
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
      
      // 停止自动播放
      isAutoPlayingRef.current = false;
      isPlayingRef.current = false;
      
      // 清除所有定时器
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
      
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      
      // 关闭录音器
      if (recorderRef.current) {
        try {
          recorderRef.current.close();
        } catch (e) {
          // 关闭录音器失败
        }
      }
      
      // 移除屏幕点击监听器
      if (screenClickHandlerRef.current) {
        document.removeEventListener('click', screenClickHandlerRef.current);
      }
      
      // 取消音频播放
      setAudioPlaybackCancelled(true);
      
      // 清空所有录音数据
      Object.values(sentenceRecordingsRef.current).forEach(recording => {
        if (recording && recording.url) {
          URL.revokeObjectURL(recording.url);
        }
      });
      
      // 组件卸载时清除 sessionStorage 中的角色扮演数据
      sessionStorage.removeItem('rolePlayData');
    };
  }, []); // ← 修复：使用空依赖数组，只在挂载/卸载时执行
  
  // 清理旧的录音 URL（避免内存泄漏）
  useEffect(() => {
    return () => {
      if (recordedAudioUrl) {
        URL.revokeObjectURL(recordedAudioUrl);
      }
    };
  }, [recordedAudioUrl]);
  
  // 同步句子录音到 ref
  useEffect(() => {
    sentenceRecordingsRef.current = sentenceRecordings;
  }, [sentenceRecordings]);

  // 同步设置到 ref
  useEffect(() => {
    readingSettingsRef.current = readingSettings;
  }, [readingSettings]);

  // 初始化：从 sessionStorage 加载角色扮演数据
  useEffect(() => {
    const initializeRolePlay = async () => {
      setLoading(true);
      
      try {
        const rolePlayDataStr = sessionStorage.getItem('rolePlayData');
        
        if (rolePlayDataStr) {
          const rolePlayData = JSON.parse(rolePlayDataStr);
          
          if (rolePlayData && rolePlayData.selectedDialogues) {
            setSelectedDialogues(rolePlayData.selectedDialogues);
            
            // 转换为对话数组格式
            const dialogueList = rolePlayData.selectedDialogues.map(dialogue => ({
              articleId: dialogue.article_id,
              articleTitle: dialogue.title,
              classify: dialogue.classify,
              sentences: dialogue.sentences || []
            }));
            
            setDialogues(dialogueList);
            
            // 获取第一个对话的所有角色
            if (dialogueList.length > 0) {
              const firstDialogue = dialogueList[0];
              const roles = extractRolesFromDialogue(firstDialogue);
              setAvailableRoles(roles);
            }
            
            // 刷新时，始终显示角色选择界面
            setIsRoleSelectionMode(true);
              setSelectedRoles([]); // 刷新后重置选择
          } else {
            setError('未找到对话数据');
          }
        } else {
          setError('未找到角色扮演数据');
        }
      } catch (err) {
        setError('角色扮演数据格式错误');
      } finally {
        setLoading(false);
      }
    };
    
    initializeRolePlay();
  }, []);

  // 从对话中提取所有唯一角色
  const extractRolesFromDialogue = (dialogue) => {
    const roleMap = new Map();
    
    dialogue.sentences.forEach(sentence => {
      if (sentence.role_id !== null && sentence.role_id !== undefined) {
        if (!roleMap.has(sentence.role_id)) {
          // 从 role_image.title 获取角色名称，并去掉冒号（如果有的话）
          let roleName = sentence.role_name || `角色${sentence.role_id}`;
          if (sentence.role_image?.title) {
            roleName = sentence.role_image.title.replace(/[:：]\s*$/, ''); // 去掉末尾的冒号（中英文）
          }
          
          roleMap.set(sentence.role_id, {
            roleId: sentence.role_id,
            roleName: roleName,
            roleImage: sentence.role_image?.res || null
          });
        }
      }
    });
    
    return Array.from(roleMap.values());
  };

  // 获取当前对话的所有唯一角色并分配位置（左/右）
  const getRolePositionMap = (dialogueId) => {
    const currentDialogue = dialogues.find(d => d.articleId === dialogueId);
    if (!currentDialogue) return {};
    
    const dialogueSentences = currentDialogue.sentences;
    
    // 提取所有唯一的 role_id
    const uniqueRoleIds = [...new Set(
      dialogueSentences
        .filter(s => s.role_id !== null && s.role_id !== undefined)
        .map(s => s.role_id)
    )];
    
    // 为每个角色分配位置：奇数索引在左，偶数索引在右
    const rolePositionMap = {};
    uniqueRoleIds.forEach((roleId, index) => {
      rolePositionMap[roleId] = index % 2 === 0 ? 'left' : 'right';
    });
    
    return rolePositionMap;
  };

  // 角色选择切换
  const toggleRoleSelection = (roleId) => {
    setSelectedRoles(prev => {
      if (prev.includes(roleId)) {
        return prev.filter(id => id !== roleId);
      } else {
        return [...prev, roleId];
      }
    });
  };

  // 开始扮演
  const startRolePlay = () => {
    if (selectedRoles.length === 0) {
      setAlertMessage('请至少选择一个角色');
      setShowAlert(true);
      return;
    }
    
    // 自动开始播放
    isAutoPlayingRef.current = true;
    setIsAutoPlaying(true);
    
    // 先设置为非角色选择模式
    setIsRoleSelectionMode(false);
    
    // 确保从第一句开始
    setCurrentSentenceIndex(0);
    
    // 延迟执行，确保状态更新后手动触发第一句的播放逻辑
    setTimeout(() => {
      console.log('startRolePlay - 延迟执行开始');
      console.log('isAutoPlayingRef.current:', isAutoPlayingRef.current);
      console.log('isRoleSelectionMode:', isRoleSelectionMode);
      console.log('currentDialogueIndex:', currentDialogueIndex);
      
      const allSentences = getAllSentences();
      console.log('allSentences:', allSentences);
      const firstSentence = allSentences[0];
      console.log('firstSentence:', firstSentence);
      
      if (firstSentence) {
        // 滚动到第一句
        const sentenceElement = document.querySelector(`[data-sentence-index="0"]`);
        if (sentenceElement) {
          sentenceElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }
        
        // 判断第一句是否为用户扮演的角色
        const isUserRole = selectedRoles.includes(firstSentence.role_id);
        console.log('isUserRole:', isUserRole, 'selectedRoles:', selectedRoles, 'role_id:', firstSentence.role_id);
        
        if (isUserRole) {
          // 扮演角色的句子：等待用户点击"说"按钮
          console.log('等待用户说话');
          setWaitingForUserSpeak(true);
        } else {
          // 非扮演角色的句子：自动播放
          console.log('开始自动播放');
          setWaitingForUserSpeak(false);
          if (playSentenceAudioRef.current) {
            console.log('调用 playSentenceAudioRef.current');
            playSentenceAudioRef.current(firstSentence);
          } else {
            console.error('playSentenceAudioRef.current 为 null');
          }
        }
      } else {
        console.error('firstSentence 为空');
      }
    }, 100);
  };

  // 获取当前对话的所有句子总数
  const getCurrentDialogueSentenceCount = useCallback(() => {
    if (dialogues.length > 0 && dialogues[currentDialogueIndex]) {
      const currentDialogue = dialogues[currentDialogueIndex];
      return currentDialogue.sentences.length;
    }
    return 0;
  }, [dialogues, currentDialogueIndex]);

  // 获取当前对话的所有句子
  const getAllSentences = useCallback(() => {
    if (dialogues.length > 0 && dialogues[currentDialogueIndex]) {
      const currentDialogue = dialogues[currentDialogueIndex];
      return currentDialogue.sentences;
    }
    return [];
  }, [dialogues, currentDialogueIndex]);

  // 篇章导航函数 - 只做篇章切换
  const previousSentence = () => {
    // 停止自动播放和当前播放（包括播放全文）
    stopAllPlayback();
    setWaitingForUserSpeak(false);
    
    if (currentDialogueIndex > 0) {
      // 切换到上一篇对话
      setCurrentDialogueIndex(prev => prev - 1);
      setCurrentSentenceIndex(0);
    }
  };

  const nextSentence = () => {
    // 停止自动播放和当前播放（包括播放全文）
    stopAllPlayback();
    setWaitingForUserSpeak(false);
    
    if (currentDialogueIndex < dialogues.length - 1) {
      // 切换到下一篇对话
      setCurrentDialogueIndex(prev => prev + 1);
      setCurrentSentenceIndex(0);
    }
  };

  // 判断是否有上一篇对话
  const hasPreviousDialogue = () => {
    return currentDialogueIndex > 0;
  };

  // 判断是否有下一篇对话
  const hasNextDialogue = () => {
    return currentDialogueIndex < dialogues.length - 1;
  };

  // 判断是否是对话的第一句
  const isFirstSentenceInDialogue = () => {
    return currentSentenceIndex === 0;
  };

  // 判断是否是对话的最后一句
  const isLastSentenceInDialogue = () => {
    const allSentences = getAllSentences();
    return currentSentenceIndex === allSentences.length - 1;
  };

  // 停止所有播放
  const stopAllPlayback = () => {
    isAutoPlayingRef.current = false;
    isPlayingRef.current = false;
    isPlayingFullDialogueRef.current = false;
    setIsAutoPlaying(false);
    setIsPlaying(false);
    setIsPlayingFullDialogue(false);
    playbackIdRef.current++;
    setAudioPlaybackCancelled(true);
    
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
      setCurrentAudio(null);
    }
    
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
      navigationTimeoutRef.current = null;
    }
    
    // 停止录音
    if (isRecording && recorderRef.current) {
      try {
        recorderRef.current.close();
      setIsRecording(false);
      } catch (e) {
        // 停止录音失败
      }
    }
    
    // 清理录音 URL
    if (recordedAudioUrl) {
      URL.revokeObjectURL(recordedAudioUrl);
      setRecordedAudioUrl(null);
    }
  };

  // 移动到下一句
  const moveToNextSentence = () => {
    const allSentences = getAllSentences();
    
    if (currentSentenceIndex < allSentences.length - 1) {
      // 移动到下一句
      setCurrentSentenceIndex(prev => prev + 1);
    } else {
      // 到达当前篇的最后一句，停止自动播放
      isAutoPlayingRef.current = false;
      setIsAutoPlaying(false);
    }
  };

  // 开始录音 - 使用TRecorder
  const startRecording = async () => {
    try {
      // 停止当前正在播放的音频，但不改变自动播放状态
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
        currentAudioRef.current = null;
        setCurrentAudio(null);
      }
      
      // 停止播放状态，但保持自动播放模式
      isPlayingRef.current = false;
      setIsPlaying(false);
      playbackIdRef.current++;
      setAudioPlaybackCancelled(true);
      
      // 重置录音时长
      setRecordingDuration(0);
      
      // 创建并打开录音器（使用与课文领读相同的高质量配置）
      const recorder = TRecorder({
        type: 'mp3',
        bitRate: 128,          // 比特率：128kbps（高音质）
        sampleRate: 44100,     // 采样率：44100Hz（CD音质）
        onProcess: function (buffers, powerLevel, duration, sampleRate) {
          setRecordingDuration(Math.floor(duration / 1000)); // 精确计时
          // powerLevel 可用于显示音量指示器（可选）
        }
      });
      
      recorderRef.current = recorder;
      
      recorder.open(function () {
        setIsRecording(true);
        
        // 延迟开始录音，避免录到点击按钮的声音
        setTimeout(() => {
          if (recorderRef.current) {
            recorder.start();
          }
        }, 300);
        
        // 注意：时长由 onProcess 回调精确更新，不需要手动计时
        
        // 添加屏幕点击监听器来结束录音
        const handleScreenClick = (e) => {
          // 点击任何地方或任何按钮都停止录音
          stopRecording();
        };
        
        screenClickHandlerRef.current = handleScreenClick;
        
        // 延迟添加监听器，避免立即触发
        setTimeout(() => {
          document.addEventListener('click', handleScreenClick);
        }, 400); // 增加延迟时间，确保录音已经开始
        
      }, function (msg, isUserNotAllow) {
        let errorMessage = '录音器打开失败';
        if (isUserNotAllow) {
          errorMessage = '用户拒绝了录音权限，请在浏览器设置中允许录音权限';
        } else if (msg.includes('getUserMedia')) {
          errorMessage = '无法访问麦克风，请检查设备连接和权限设置';
        } else if (msg.includes('NotAllowedError')) {
          errorMessage = '录音权限被拒绝，请允许网站使用麦克风';
        } else if (msg.includes('NotFoundError')) {
          errorMessage = '未找到可用的录音设备，请检查麦克风连接';
        } else {
          errorMessage = '录音器打开失败：' + msg;
        }
        
        setAlertMessage(errorMessage);
        setShowAlert(true);
      });
      
    } catch (error) {
      setAlertMessage('启动录音失败：' + error.message);
      setShowAlert(true);
    }
  };

  // 停止录音并获取音频数据
  const stopRecording = async () => {
    if (!recorderRef.current || !isRecording) {
      return;
    }
    
    try {
      const recorder = recorderRef.current;
      
      // 立即移除点击监听器，避免重复触发
      if (screenClickHandlerRef.current) {
        document.removeEventListener('click', screenClickHandlerRef.current);
        screenClickHandlerRef.current = null;
      }
      
      recorder.stop(async function (blob, duration) {
        setIsRecording(false);
        
        // 清除计时器
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
        
        // 检查录音时长
        const durationSeconds = Math.floor(duration / 1000);
        if (durationSeconds < 1) {
          setAlertMessage('录音时间太短，请至少录制1秒钟');
          setShowAlert(true);
          return;
        }
        
        if (durationSeconds > 60) {
          setAlertMessage('录音时间过长，请录制60秒以内的音频');
          setShowAlert(true);
          return;
        }
        
        // 检查音频数据
        if (!blob || blob.size === 0) {
          setAlertMessage('录音数据为空，请重新录音');
          setShowAlert(true);
          return;
        }
        
        // 直接使用 TRecorder 生成的 MP3 blob，不进行格式转换
        // 注意：TRecorder 已经配置为 MP3 格式和 44100Hz 采样率
        // 不进行裁剪，以保持原始的 MP3 格式和采样率
        
        // 获取当前句子内容用于评测
        const allSentences = getAllSentences();
        const currentSentence = allSentences[currentSentenceIndex];
        
        if (currentSentence && currentSentence.title) {
          evaluateVoice(blob, currentSentence.title);
        } else {
          setAlertMessage('当前句子数据异常，无法进行评测');
          setShowAlert(true);
        }
        
      }, function (msg) {
        setIsRecording(false);
        
        // 清除计时器
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
        
        setAlertMessage('录音停止失败：' + msg);
        setShowAlert(true);
      });
      
      recorderRef.current = null;
      
    } catch (error) {
      setIsRecording(false);
      
      // 清除计时器
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      
      // 移除屏幕点击监听器
      if (screenClickHandlerRef.current) {
        document.removeEventListener('click', screenClickHandlerRef.current);
        screenClickHandlerRef.current = null;
      }
      
      setAlertMessage('停止录音失败：' + error.message);
      setShowAlert(true);
    }
  };

  // 语音评测 API 调用
  const evaluateVoice = async (audioBlob, content) => {
    try {
      // 验证输入参数
      if (!audioBlob || audioBlob.size === 0) {
        throw new Error('录音数据为空，请重新录音');
      }
      
      if (!content || content.trim() === '') {
        throw new Error('评测文本为空');
      }
      
      // 检查音频大小（限制为10MB）
      if (audioBlob.size > 10 * 1024 * 1024) {
        throw new Error('录音文件过大，请录制较短的音频');
      }
      
      // 创建音频 URL 用于播放
      const audioUrl = URL.createObjectURL(audioBlob);
      setRecordedAudioUrl(audioUrl);
      recordedAudioUrlRef.current = audioUrl; // 同时保存到 ref
      
      // 先播放用户录音，播放完成后再进行评测
      playRecordedAudio(audioUrl, audioBlob, content);
      
    } catch (error) {
      setAlertMessage(error.message);
      setShowAlert(true);
      
      // 清理录音 URL
      if (recordedAudioUrlRef.current) {
        URL.revokeObjectURL(recordedAudioUrlRef.current);
        setRecordedAudioUrl(null);
        recordedAudioUrlRef.current = null;
      }
    }
  };
  
  // 实际的评测 API 调用
  const performEvaluation = async (audioBlob, content) => {
    setIsEvaluating(true);
    setEvaluationResult(null);
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.mp3');
      formData.append('content', content);
      formData.append('eval_type', 2); // 角色扮演对话评测
      formData.append('user_id', 'user12345'); // 可以从用户状态获取
      formData.append('audio_type', 1); // MP3格式
      
      // 设置请求超时
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30秒超时
      
      const result = await voiceAPI.evaluate(formData, controller.signal);
      clearTimeout(timeoutId);
      
      if (result.code === 200) {
        if (result.data && typeof result.data.score === 'number') {
          setEvaluationResult(result.data);
          
          // 根据分数显示对应的图片
          const score = result.data.score;
          let imageType = '';
          if (score < 60) {
            imageType = '测评差';
          } else if (score >= 60 && score < 80) {
            imageType = '测评中';
          } else if (score >= 80) {
            imageType = '测评好';
          }
          
          setScoreImageType(imageType);
          setShowScoreImage(true);
          
          // 1.5秒后隐藏图片，然后继续下一句
          setTimeout(() => {
            setShowScoreImage(false);
            
            // 再等待200ms后继续（让图片消失动画完成）
            setTimeout(() => {
              // 保存当前句子的录音到 sentenceRecordings
              const allSentences = getAllSentences();
              const currentSentence = allSentences[currentSentenceIndex];
              if (currentSentence && audioBlob) {
                // 创建新的 URL 用于长期保存
                const permanentUrl = URL.createObjectURL(audioBlob);
                setSentenceRecordings(prev => ({
                  ...prev,
                  [currentSentenceIndex]: {
                    blob: audioBlob,
                    url: permanentUrl,
                    score: score  // 保存分数
                  }
                }));
              }
              
              // 清理录音 URL（使用 ref 确保能正确清理）
              if (recordedAudioUrlRef.current) {
                URL.revokeObjectURL(recordedAudioUrlRef.current);
                setRecordedAudioUrl(null);
                recordedAudioUrlRef.current = null;
              }
              
              // 如果在自动播放模式，继续下一句
              if (isAutoPlayingRef.current) {
                const pauseTime = readingSettingsRef.current.pauseTime * 1000;
                navigationTimeoutRef.current = setTimeout(() => {
                  moveToNextSentence();
                }, pauseTime);
              }
            }, 200);
          }, 1500);
        } else {
          throw new Error('评测结果格式异常');
        }
      } else {
        throw new Error(result.message || '评测失败');
      }
      
    } catch (error) {
      let errorMessage = '语音评测失败';
      if (error.name === 'AbortError') {
        errorMessage = '评测请求超时，请检查网络连接';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage = '网络连接失败，请检查评测服务是否启动';
      } else {
        errorMessage = error.message;
      }
      
      setAlertMessage(errorMessage);
      setShowAlert(true);
      setEvaluationResult({
        error: true,
        message: errorMessage
      });
      
      // 评测失败时，清理录音 URL（使用 ref 确保能正确清理）
      if (recordedAudioUrlRef.current) {
        URL.revokeObjectURL(recordedAudioUrlRef.current);
        setRecordedAudioUrl(null);
        recordedAudioUrlRef.current = null;
      }
      
      // 即使评测失败，在自动播放模式下也继续下一句
      if (isAutoPlayingRef.current) {
        const pauseTime = readingSettingsRef.current.pauseTime * 1000;
        navigationTimeoutRef.current = setTimeout(() => {
          moveToNextSentence();
        }, pauseTime);
      }
    } finally {
      setIsEvaluating(false);
    }
  };

  // 播放录音
  const playRecordedAudio = async (audioUrl, audioBlob = null, content = null) => {
    try {
      // 重置播放取消标志，允许后续播放
      setAudioPlaybackCancelled(false);
      
      const audio = new Audio(audioUrl);
      currentAudioRef.current = audio;
      setCurrentAudio(audio);
      setIsPlaying(true);
      setWaitingForUserSpeak(false);
      
      // 设置播放结束回调
      audio.onended = () => {
        currentAudioRef.current = null;
        setCurrentAudio(null);
        setIsPlaying(false);
        
        // 播放完成后，如果有 audioBlob 和 content，则进行评测
        if (audioBlob && content) {
          performEvaluation(audioBlob, content);
        } else {
          // 如果没有评测数据，直接清理并继续
          if (recordedAudioUrlRef.current) {
            URL.revokeObjectURL(recordedAudioUrlRef.current);
            setRecordedAudioUrl(null);
            recordedAudioUrlRef.current = null;
          }
          
          // 如果在自动播放模式，继续下一句
        if (isAutoPlayingRef.current) {
          const pauseTime = readingSettingsRef.current.pauseTime * 1000;
          navigationTimeoutRef.current = setTimeout(() => {
            moveToNextSentence();
          }, pauseTime);
          }
        }
      };
      
      audio.onerror = (e) => {
        currentAudioRef.current = null;
        setCurrentAudio(null);
        setIsPlaying(false);
        setWaitingForUserSpeak(false);
        
        // 清理录音 URL（使用 ref 确保能正确清理）
        if (recordedAudioUrlRef.current) {
          URL.revokeObjectURL(recordedAudioUrlRef.current);
          setRecordedAudioUrl(null);
          recordedAudioUrlRef.current = null;
        }
        
        // 播放失败时，如果有评测数据，仍然进行评测
        if (audioBlob && content) {
          performEvaluation(audioBlob, content);
        } else if (isAutoPlayingRef.current) {
          // 否则继续下一句
          const pauseTime = readingSettingsRef.current.pauseTime * 1000;
          navigationTimeoutRef.current = setTimeout(() => {
            moveToNextSentence();
          }, pauseTime);
        }
      };
      
      await audio.play();
      
    } catch (error) {
      setCurrentAudio(null);
      setIsPlaying(false);
      setWaitingForUserSpeak(false);
      
      // 清理录音 URL（使用 ref 确保能正确清理）
      if (recordedAudioUrlRef.current) {
        URL.revokeObjectURL(recordedAudioUrlRef.current);
        setRecordedAudioUrl(null);
        recordedAudioUrlRef.current = null;
      }
      
      // 播放失败时，如果有评测数据，仍然进行评测
      if (audioBlob && content) {
        performEvaluation(audioBlob, content);
      } else if (isAutoPlayingRef.current) {
        // 否则继续下一句
        const pauseTime = readingSettingsRef.current.pauseTime * 1000;
        navigationTimeoutRef.current = setTimeout(() => {
          moveToNextSentence();
        }, pauseTime);
      }
    }
  };

  // 播放句子音频
  const playSentenceAudio = useCallback(async (sentence, customSettings = null) => {
    const currentPlaybackId = ++playbackIdRef.current;
    setAudioPlaybackCancelled(false);
    
    const currentSettings = customSettings || readingSettingsRef.current;
    
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
      setCurrentAudio(null);
    }
    
    if (!sentence || !sentence.audio || !sentence.audio.res) {
      return;
    }
    
    if (currentPlaybackId !== playbackIdRef.current) {
      return;
    }
    
    if (!isPlayingRef.current) {
      isPlayingRef.current = true;
      setIsPlaying(true);
    }
    
    try {
      const audioUrl = sentence.audio.res;
      
      // 在角色扮演模式下，非玩家角色的音频强制只播放一次
      const isRolePlayMode = !isRoleSelectionMode && selectedRoles.length > 0;
      const playCount = isRolePlayMode ? 1 : currentSettings.playCount;
      const repeatCount = isRolePlayMode ? 'playAfterAll' : currentSettings.repeatCount;
      const pauseTimeMultiplier = currentSettings.pauseTime;
      
      const testResponse = await fetch(audioUrl, { method: 'HEAD' });
      if (!testResponse.ok) {
        throw new Error(`音频文件不可访问: ${testResponse.status}`);
      }
      
      const audio = new Audio(audioUrl);
      currentAudioRef.current = audio;
      setCurrentAudio(audio);
      
      const playbackRate = currentSettings.playSpeed === 'fast' ? 1.5 : 
                          currentSettings.playSpeed === 'slow' ? 0.75 : 1;
      audio.playbackRate = playbackRate;
      
      // 等待音频元数据加载
      await new Promise((resolve, reject) => {
        audio.onloadedmetadata = () => {
          audio.playbackRate = playbackRate;
          resolve();
        };
        audio.onerror = reject;
        audio.load();
      });
      
      // 等待音频数据加载完成（readyState >= 3）
      if (audio.readyState < 3) {
        await new Promise((resolve, reject) => {
          audio.oncanplaythrough = () => {
            resolve();
          };
          audio.onerror = reject;
          // 如果已经在加载中，这会触发 canplaythrough
          if (audio.readyState < 2) {
            audio.load();
          }
        });
      }
      
      const audioDuration = audio.duration;
      const pauseTime = audioDuration * pauseTimeMultiplier * 1000;

      // 根据跟读次数模式选择不同的播放逻辑
      if (repeatCount === 'playOneReadOne') {
        // 播1读1：播放一次 -> 暂停 -> 播放一次 -> 暂停...
        for (let i = 0; i < playCount; i++) {
          if (currentPlaybackId !== playbackIdRef.current || audioPlaybackCancelled) {
            break;
          }
          
          // 播放音频
          audio.currentTime = 0;
          await new Promise((resolve, reject) => {
            audio.onended = () => {
              resolve();
            };
            audio.onerror = (e) => {
              reject(e);
            };
            const playPromise = audio.play();
            if (playPromise !== undefined) {
              playPromise.catch(e => {
                reject(e);
              });
            }
          });
          
          if (currentPlaybackId !== playbackIdRef.current || audioPlaybackCancelled) {
            break;
          }
          
          // 暂停间隔
          await new Promise(resolve => setTimeout(resolve, pauseTime));
        }
      } else if (repeatCount === 'playTwoReadOne') {
        // 播2读1：播放两次 -> 暂停一次
        const totalCycles = Math.ceil(playCount / 2);
        let remainingPlays = playCount;
        
        for (let cycle = 0; cycle < totalCycles; cycle++) {
          const playsInThisCycle = Math.min(2, remainingPlays);
          
          // 播放1-2次
          for (let i = 0; i < playsInThisCycle; i++) {
            if (currentPlaybackId !== playbackIdRef.current || audioPlaybackCancelled) {
              break;
            }
            
            audio.currentTime = 0;
            await new Promise((resolve, reject) => {
              audio.onended = () => {
                resolve();
              };
              audio.onerror = (e) => {
                reject(e);
              };
              const playPromise = audio.play();
              if (playPromise !== undefined) {
                playPromise.catch(e => {
                  reject(e);
                });
              }
            });
            
            // 两次播放之间的间隔
            if (i < playsInThisCycle - 1) {
              await new Promise(resolve => setTimeout(resolve, pauseTime * 0.5));
            }
          }
          
          if (currentPlaybackId !== playbackIdRef.current || audioPlaybackCancelled) {
            break;
          }
          
          // 暂停间隔（只在不是最后一个循环时）
          if (cycle < totalCycles - 1) {
            await new Promise(resolve => setTimeout(resolve, pauseTime));
          }
          
          remainingPlays -= playsInThisCycle;
        }
      } else if (repeatCount === 'continuous') {
        // 连续播放：播放多次，中间只有短暂间隔
        for (let i = 0; i < playCount; i++) {
          if (currentPlaybackId !== playbackIdRef.current || audioPlaybackCancelled) {
            break;
          }
          
          audio.currentTime = 0;
          
          await new Promise((resolve, reject) => {
            let hasEnded = false;
            
            audio.onended = () => {
              if (hasEnded) {
                return;
              }
              hasEnded = true;
              resolve();
            };
            
            audio.onerror = (e) => {
              reject(e);
            };
            
            const playPromise = audio.play();
            
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  // 播放已开始
                })
                .catch(e => {
                  reject(e);
                });
            }
          });
          
          // 连续播放时的短暂间隔
          if (i < playCount - 1) {
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        }
      } else if (repeatCount === 'playAfterAll') {
        // 播完后读：只播放音频，不需要暂停让用户跟读
        
        for (let i = 0; i < playCount; i++) {
          if (currentPlaybackId !== playbackIdRef.current || audioPlaybackCancelled) {
            break;
          }
          
          audio.currentTime = 0;
          
          await new Promise((resolve, reject) => {
            audio.onended = () => {
              resolve();
            };
            audio.onerror = (e) => {
              reject(e);
            };
            const playPromise = audio.play();
            if (playPromise !== undefined) {
              playPromise.catch(e => {
                reject(e);
              });
            }
          });
          
          // 播放之间的短暂间隔
          if (i < playCount - 1) {
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        }
        
        // playAfterAll 模式不需要暂停让用户跟读，直接进入下一句
      } else {
        // 未知的播放模式，使用默认行为（播放一次）
        audio.currentTime = 0;
        await new Promise((resolve, reject) => {
          audio.onended = () => {
            resolve();
          };
          audio.onerror = (e) => {
            reject(e);
          };
          const playPromise = audio.play();
          if (playPromise !== undefined) {
            playPromise.catch(e => {
              reject(e);
            });
          }
        });
      }
      
      // 播放完成后的处理
      if (currentPlaybackId === playbackIdRef.current && !audioPlaybackCancelled) {
        currentAudioRef.current = null;
        setCurrentAudio(null);
        isPlayingRef.current = false;
        setIsPlaying(false);
        
        // 自动播放模式：根据情况决定是否等待
        if (isAutoPlayingRef.current) {
          // 在角色扮演模式下，非玩家角色播放完立即进入下一句
          const isRolePlayMode = !isRoleSelectionMode && selectedRoles.length > 0;
          const waitTime = isRolePlayMode ? 0 : pauseTime;
          
          navigationTimeoutRef.current = setTimeout(() => {
            moveToNextSentence();
          }, waitTime);
        }
      }
    } catch (error) {
      isPlayingRef.current = false;
      setIsPlaying(false);
      currentAudioRef.current = null;
      setCurrentAudio(null);
    }
  }, [audioPlaybackCancelled, moveToNextSentence]);
  
  // 创建 ref 来打破循环依赖
  playSentenceAudioRef.current = playSentenceAudio;

  // 监听当前句子索引变化，更新最大已访问句子索引
  useEffect(() => {
    if (currentSentenceIndex > maxVisitedSentenceIndex) {
      setMaxVisitedSentenceIndex(currentSentenceIndex);
    }
  }, [currentSentenceIndex, maxVisitedSentenceIndex]);

  // 监听当前句子索引变化，自动处理播放
  useEffect(() => {
    if (!isRoleSelectionMode && isAutoPlayingRef.current && dialogues.length > 0) {
      const allSentences = getAllSentences();
      const currentSentence = allSentences[currentSentenceIndex];
      
      if (currentSentence) {
        // 判断当前句子是否为用户扮演的角色
        const isUserRole = selectedRoles.includes(currentSentence.role_id);
        
        // 滚动到当前句子
        setTimeout(() => {
          const sentenceElement = document.querySelector(`[data-sentence-index="${currentSentenceIndex}"]`);
          if (sentenceElement) {
            sentenceElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest'
            });
          }
        }, 100);
        
        if (isUserRole) {
          // 扮演角色的句子：等待用户点击"说"按钮
          setWaitingForUserSpeak(true);
        } else {
          // 非扮演角色的句子：自动播放
          setWaitingForUserSpeak(false);
          playSentenceAudioRef.current(currentSentence);
        }
      }
    }
  }, [currentSentenceIndex, currentDialogueIndex, isRoleSelectionMode, dialogues, getAllSentences, selectedRoles]);

  // 监听对话切换，重置句子索引并滚动到第一句
  useEffect(() => {
    if (dialogues.length > 0 && prevDialogueIndexRef.current !== currentDialogueIndex) {
      prevDialogueIndexRef.current = currentDialogueIndex;
      
      setCurrentSentenceIndex(0);
      setMaxVisitedSentenceIndex(0); // 重置最大已访问句子索引
      
      // 更新当前对话的可用角色
      const currentDialogue = dialogues[currentDialogueIndex];
      if (currentDialogue) {
        const roles = extractRolesFromDialogue(currentDialogue);
        setAvailableRoles(roles);
      }
      
      // 切换到新对话时，重新进入角色选择模式
      setIsRoleSelectionMode(true);
      setSelectedRoles([]);
      
      // 清空所有录音数据
      Object.values(sentenceRecordingsRef.current).forEach(recording => {
        if (recording && recording.url) {
          URL.revokeObjectURL(recording.url);
        }
      });
      setSentenceRecordings({});
      sentenceRecordingsRef.current = {};
      
      // 停止自动播放
      isAutoPlayingRef.current = false;
      setIsAutoPlaying(false);
      setWaitingForUserSpeak(false);
      
      // 延迟执行以确保 DOM 已更新
      const timer = setTimeout(() => {
        const sentenceElement = document.querySelector(`[data-sentence-index="0"]`);
        if (sentenceElement) {
          sentenceElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [currentDialogueIndex, dialogues]);

  // 滚动到当前句子
  const scrollToCurrentSentence = useCallback((sentenceIndex) => {
    setTimeout(() => {
      const sentenceElement = document.querySelector(`[data-sentence-index="${sentenceIndex}"]`);
      if (sentenceElement) {
        sentenceElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }
    }, 150);
  }, []);

  // 切换自动播放
  const toggleAutoPlay = () => {
    if (isAutoPlayingRef.current) {
      // 停止自动播放
      stopAllPlayback();
      setWaitingForUserSpeak(false);
    } else {
      // 开始自动播放
      isAutoPlayingRef.current = true;
      setIsAutoPlaying(true);
      
      const allSentences = getAllSentences();
      const currentSentence = allSentences[currentSentenceIndex];
      
      if (currentSentence) {
        const isUserRole = selectedRoles.includes(currentSentence.role_id);
        
        if (isUserRole) {
          // 扮演角色的句子：等待用户点击"说"按钮
          setWaitingForUserSpeak(true);
        } else {
          // 非扮演角色的句子：自动播放
          setWaitingForUserSpeak(false);
          playSentenceAudio(currentSentence);
        }
      }
    }
  };

  // 句子点击处理
  const handleSentenceClick = (sentenceIndex) => {
    // 如果正在播放全文，禁止点击句子
    if (isPlayingFullDialogue) {
      return;
    }
    
    // 检查是否点击的是当前正在播放的句子
    const isCurrentSentence = sentenceIndex === currentSentenceIndex;
    
    // 如果点击的是当前正在播放的句子，且正在自动播放，则不做任何操作
    if (isCurrentSentence && isAutoPlayingRef.current && isPlayingRef.current) {
      return;
    }
    
    // 停止当前音频播放
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
      setCurrentAudio(null);
    }
    
    // 增加 playbackId 以取消正在进行的播放，并重置取消标志
    playbackIdRef.current++;
    setAudioPlaybackCancelled(false);
    
    // 清除导航定时器
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
      navigationTimeoutRef.current = null;
    }
    
    // 更新当前句子索引并滚动到该句子
    setCurrentSentenceIndex(sentenceIndex);
    scrollToCurrentSentence(sentenceIndex);
    
    // 如果之前是自动播放模式，继续自动播放
    if (isAutoPlayingRef.current) {
      // 延迟一点时间让UI更新完成，然后继续播放
      setTimeout(() => {
        if (isAutoPlayingRef.current) {
          playNextSentence();
        }
      }, 100);
    }
  };

  // 句子播放按钮点击
  const handleSentencePlayClick = (sentenceIndex) => {
    // 如果正在播放全文，禁止点击句子
    if (isPlaying) {
      return;
    }
    
    // 停止自动播放
    isAutoPlayingRef.current = false;
    setIsAutoPlaying(false);
    setWaitingForUserSpeak(false);
    
    // 更新当前句子索引并滚动到该句子
    setCurrentSentenceIndex(sentenceIndex);
    scrollToCurrentSentence(sentenceIndex);
    
    // 播放该句子
    const allSentences = getAllSentences();
    if (allSentences[sentenceIndex]) {
      playSentenceAudio(allSentences[sentenceIndex]);
    }
  };

  // 播放当前句子
  const handlePlayCurrentSentence = () => {
    const allSentences = getAllSentences();
    const currentSentence = allSentences[currentSentenceIndex];
    
    if (currentSentence) {
      const isUserRole = selectedRoles.includes(currentSentence.role_id);
      
      if (isUserRole) {
        // 如果是扮演角色，不播放原音频
        setAlertMessage('这是您扮演的角色，请点击"说"按钮进行跟读');
        setShowAlert(true);
      } else {
        // 停止自动播放
        isAutoPlayingRef.current = false;
        setIsAutoPlaying(false);
        
        // 播放当前句子
        playSentenceAudio(currentSentence);
      }
    }
  };

  // 处理用户点击"说"按钮
  const handleUserSpeak = () => {
    if (isRecording) {
      // 正在录音，点击停止录音
      stopRecording();
    } else {
      // 开始录音
      startRecording();
    }
  };

  // 切换翻译显示
  const handleToggleTranslation = () => {
    setShowTranslation(prev => !prev);
  };

  // 切换到指定对话
  const switchToDialogue = (dialogueId) => {
    const index = dialogues.findIndex(d => d.articleId === dialogueId);
    if (index !== -1 && index !== currentDialogueIndex) {
      // 停止当前播放
      stopAllPlayback();
      setWaitingForUserSpeak(false);
      
      // 切换到指定对话
      setCurrentDialogueIndex(index);
      setCurrentSentenceIndex(0);
    }
  };

  // 返回同步课文
  const handleBack = () => {
    // 停止所有播放
    stopAllPlayback();
    
    // 导航回同步课文页面
    navigate('/text');
  };

  // 打开领读设置
  const handleOpenReadingSettings = () => {
    setShowReadingSettings(true);
  };

  // 关闭领读设置
  const handleReadingSettingsClose = () => {
    setShowReadingSettings(false);
  };

  // 应用领读设置
  const handleReadingSettingsUpdate = (newSettings) => {
    setReadingSettings(newSettings);
    setShowReadingSettings(false);
  };
  
  // 检查用户是否完成了所有扮演角色的录音
  const checkAllRoleRecordingsComplete = () => {
    const allSentences = getAllSentences();
    for (let i = 0; i < allSentences.length; i++) {
      const sentence = allSentences[i];
      const isUserRole = selectedRoles.includes(sentence.role_id);
      if (isUserRole && !sentenceRecordingsRef.current[i]) {
        return false; // 有扮演角色的句子未录音
      }
    }
    return true;
  };
  
  // 播放整篇对话
  const playFullDialogue = async () => {
    // 检查是否完成了所有录音
    if (!checkAllRoleRecordingsComplete()) {
      setAlertMessage('请先完成所有扮演角色的录音');
      setShowAlert(true);
      return;
    }
    
    // 停止当前播放
    stopAllPlayback();
    
    const allSentences = getAllSentences();
    if (allSentences.length === 0) return;
    
    // 设置播放状态（包括播放全文状态）
    isPlayingRef.current = true;
    isPlayingFullDialogueRef.current = true;
    setIsPlaying(true);
    setIsPlayingFullDialogue(true);
    
    // 从第一句开始播放
    await playFullDialogueSentence(0, allSentences);
  };
  
  // 递归播放整篇对话的每个句子
  const playFullDialogueSentence = async (index, allSentences) => {
    if (index >= allSentences.length) {
      // 播放完成，停留在最后一句
      isPlayingRef.current = false;
      isPlayingFullDialogueRef.current = false;
      setIsPlaying(false);
      setIsPlayingFullDialogue(false);
      setCurrentSentenceIndex(allSentences.length - 1);
      scrollToCurrentSentence(allSentences.length - 1);
      return;
    }
    
    const sentence = allSentences[index];
    const isUserRole = selectedRoles.includes(sentence.role_id);
    const hasRecording = sentenceRecordingsRef.current[index];
    
    // 更新当前句子索引（用于视觉反馈）
    setCurrentSentenceIndex(index);
    
    // 滚动到当前句子
    scrollToCurrentSentence(index);
    
    try {
      if (isUserRole && hasRecording) {
        // 播放用户录音
        const recordingData = sentenceRecordingsRef.current[index];
        const audio = new Audio(recordingData.url);
        currentAudioRef.current = audio;
        setCurrentAudio(audio);
        
        await new Promise((resolve, reject) => {
          audio.onended = resolve;
          audio.onerror = reject;
          audio.play().catch(reject);
        });
      } else if (!isUserRole) {
        // 播放原始音频（非用户扮演的角色）
        if (sentence.audio && sentence.audio.res) {
          const audio = new Audio(sentence.audio.res);
          currentAudioRef.current = audio;
          setCurrentAudio(audio);
          
          await new Promise((resolve, reject) => {
            audio.onended = resolve;
            audio.onerror = reject;
            audio.play().catch(reject);
          });
        }
      }
      // 如果是用户角色但没有录音，跳过这句
      
      // 播放完当前句子后，等待一小段时间再播放下一句
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // 继续播放下一句
      if (isPlayingRef.current) {
        await playFullDialogueSentence(index + 1, allSentences);
      }
    } catch (error) {
      // 播放出错，停止播放
      isPlayingRef.current = false;
      setIsPlaying(false);
      currentAudioRef.current = null;
      setCurrentAudio(null);
    }
  };

  // 速度变化处理
  const handleSpeedChange = (speed) => {
    setReadingSettings(prev => ({
      ...prev,
      playSpeed: speed
    }));
  };

  // 如果正在加载，显示加载状态
  if (loading) {
    return (
      <>
        <div className="role-play-container">
          <div className="role-play-loading">
            <div className="loading-spinner"></div>
            <p>加载中...</p>
          </div>
        </div>
        <Alert
          show={showAlert}
          message={alertMessage}
          onHide={() => setShowAlert(false)}
          type="warning"
          duration={1200}
        />
      </>
    );
  }

  // 如果有错误，显示错误信息
  if (error) {
    return (
      <>
        <div className="role-play-container">
          <div className="role-play-error">
            <p>{error}</p>
            <button onClick={handleBack}>返回同步课文</button>
          </div>
        </div>
        <Alert
          show={showAlert}
          message={alertMessage}
          onHide={() => setShowAlert(false)}
          type="warning"
          duration={1200}
        />
      </>
    );
  }

  // 角色选择模式
  if (isRoleSelectionMode && dialogues.length > 0) {
    const currentDialogue = dialogues[currentDialogueIndex];
    
    return (
      <>
      <div className="role-play-container">
        {/* Logo区域 */}
        <div className="role-play-header">
          <img 
            src={logoImage} 
            alt="大鱼人机口语" 
            className="role-play-logo"
          />
        </div>
        
        {/* 左侧区域 */}
        <div className="role-play-left">
          {/* 第一部分 - 角色选择区 */}
          <div className="role-play-section-1">
            <div className="role-selection-content">
              {/* 对话标题 */}
              <h2 className="role-selection-title">{currentDialogue.articleTitle}</h2>
              
              {/* 选择提示 */}
              <p className="role-selection-instruction">选择要扮演的角色（可多选或单选）</p>
              
              {/* 角色列表 */}
              <div className="roles-container">
                {availableRoles.map(role => (
                  <div
                    key={role.roleId}
                    className={`role-item ${selectedRoles.includes(role.roleId) ? 'selected' : ''}`}
                    onClick={() => toggleRoleSelection(role.roleId)}
                  >
                    {/* 角色头像 */}
                    <div className="role-avatar">
                      {role.roleImage ? (
                        <img src={role.roleImage} alt={role.roleName} />
                      ) : (
                        <div className="avatar-placeholder">👤</div>
                      )}
                    </div>
                    
                    {/* 角色名称 */}
                    <div className="role-name">{role.roleName}</div>
                    
                    {/* 选中标记 */}
                    {selectedRoles.includes(role.roleId) && (
                      <div className="role-selected-badge">✓</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* 第二部分 - 按钮区 */}
          <div className="role-play-section-2">
            <div className="role-play-controls">
              <button 
                className="rp-control-btn start-role-play-btn"
                onClick={startRolePlay}
              >
                开始扮演
              </button>
              
              <button 
                className="rp-control-btn rp-back-btn"
                onClick={handleBack}
              >
                返回同步课文
              </button>
            </div>
          </div>
        </div>
        
        {/* 右侧区域 - 已选对话列表 */}
        <div className="role-play-right">
          <div className="role-play-dialogues-list">
            <div className="dialogues-list-header">
              <h3>已选对话 ({selectedDialogues.length})</h3>
            </div>
            <div className="dialogues-list-content">
              {selectedDialogues.map((dialogue, index) => {
                const isCurrentDialogue = dialogues.length > 0 && 
                  dialogues[currentDialogueIndex] && 
                  dialogues[currentDialogueIndex].articleId === dialogue.article_id;
                
                return (
                  <div 
                    key={dialogue.article_id} 
                    className={`dialogue-list-item ${isCurrentDialogue ? 'active' : ''}`}
                    onClick={() => switchToDialogue(dialogue.article_id)}
                  >
                    <div className="dialogue-list-item-title">{dialogue.title}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      
      {/* 提示框 */}
      <Alert
        show={showAlert}
        message={alertMessage}
        onHide={() => setShowAlert(false)}
        type="warning"
        duration={1200}
      />
      </>
    );
  }

  // 角色扮演模式
  return (
    <>
    <div className="role-play-container">
      {/* Logo区域 */}
      <div className="role-play-header">
        <img 
          src={logoImage} 
          alt="大鱼人机口语" 
          className="role-play-logo"
        />
      </div>
      
      {/* 左侧区域 */}
      <div className="role-play-left">
        {/* 第一部分 - 句子显示区（可滚动） */}
        <div className={`role-play-section-1 ${isPlayingFullDialogue ? 'playing' : ''}`}>
          <div className="role-play-content-scrollable">
            {/* 只显示当前对话 */}
            {dialogues.length > 0 && dialogues[currentDialogueIndex] && (() => {
              const currentDialogue = dialogues[currentDialogueIndex];
              const rolePositionMap = getRolePositionMap(currentDialogue.articleId);
              
              return (
                <div key={currentDialogue.articleId} className="dialogue-section">
                  {/* 对话标题 */}
                  <div className="role-play-title-header">
                    <h2 className="role-play-title">{currentDialogue.articleTitle}</h2>
                  </div>
                  
                  {/* 对话句子列表 - 显示到最大已访问的句子 */}
                  <div className="role-play-dialogue-display">
                    {currentDialogue.sentences.slice(0, maxVisitedSentenceIndex + 1).map((sentence, sentenceIndex) => {
                      const position = sentence.role_id ? rolePositionMap[sentence.role_id] : 'left';
                      const isSelectedRole = selectedRoles.includes(sentence.role_id);
                      const isCurrentSentence = sentenceIndex === currentSentenceIndex;
                      
                      return (
                        <div 
                          key={sentenceIndex} 
                          className={`dialogue-row dialogue-${position} ${isCurrentSentence ? 'current-sentence' : ''} ${isSelectedRole ? 'user-role' : ''}`}
                          data-sentence-index={sentenceIndex}
                        >
                          {/* 头像 */}
                          <div className="dialogue-avatar">
                            {sentence.role_image && sentence.role_image.res ? (
                              <img src={sentence.role_image.res} alt={sentence.role_image.title || '角色'} />
                            ) : (
                              <div className="avatar-placeholder">👤</div>
                            )}
                          </div>
                          
                          {/* 对话气泡 */}
                          <div className="dialogue-bubble-container">
                            <div 
                              className={`dialogue-bubble ${isCurrentSentence && isPlaying ? 'playing' : ''} ${isSelectedRole ? 'user-bubble' : ''} ${isPlayingFullDialogue ? 'disabled' : ''} ${
                                isSelectedRole && sentenceRecordings[sentenceIndex] && sentenceRecordings[sentenceIndex].score !== undefined
                                  ? sentenceRecordings[sentenceIndex].score < 60
                                    ? 'score-low'
                                    : sentenceRecordings[sentenceIndex].score < 80
                                    ? 'score-medium'
                                    : 'score-high'
                                  : ''
                              }`}
                              onClick={() => !isSelectedRole && !isPlayingFullDialogue && handleSentenceClick(sentenceIndex)}
                              style={{ cursor: isPlayingFullDialogue ? 'not-allowed' : (isSelectedRole ? 'default' : 'pointer') }}
                            >
                              {/* 玩家角色在左侧时的装饰标签 - 评测完成后显示 */}
                              {isSelectedRole && position === 'left' && sentenceRecordings[sentenceIndex] && sentenceRecordings[sentenceIndex].score !== undefined && (
                                <div className={`user-role-tag ${
                                  sentenceRecordings[sentenceIndex].score < 60
                                    ? 'score-low'
                                    : sentenceRecordings[sentenceIndex].score < 80
                                    ? 'score-medium'
                                    : 'score-high'
                                }`}>
                                  {sentenceRecordings[sentenceIndex].score < 60
                                    ? 'Fighting'
                                    : sentenceRecordings[sentenceIndex].score < 80
                                    ? 'Good'
                                    : 'Perfect'}
                                </div>
                              )}
                              
                              {/* 玩家角色在右侧时的装饰标签 - 评测完成后显示 */}
                              {isSelectedRole && position === 'right' && sentenceRecordings[sentenceIndex] && sentenceRecordings[sentenceIndex].score !== undefined && (
                                <div className={`user-role-tag-right ${
                                  sentenceRecordings[sentenceIndex].score < 60
                                    ? 'score-low'
                                    : sentenceRecordings[sentenceIndex].score < 80
                                    ? 'score-medium'
                                    : 'score-high'
                                }`}>
                                  {sentenceRecordings[sentenceIndex].score < 60
                                    ? 'Fighting'
                                    : sentenceRecordings[sentenceIndex].score < 80
                                    ? 'Good'
                                    : 'Perfect'}
                                </div>
                              )}
                              
                              <div className="dialogue-content">
                                <div className="dialogue-sentence-english">
                                  {formatSentenceText(sentence.title)}
                                </div>
                                {showTranslation && sentence.en_title && (
                                  <div className="dialogue-sentence-translation">
                                    {sentence.en_title}
                                  </div>
                                )}
                              </div>
                              
                              {/* 句子播放按钮（只显示在非选中角色的句子上） */}
                              {!isSelectedRole && (
                                <button 
                                  className={`sentence-play-btn ${isCurrentSentence && isPlaying ? 'playing' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!isPlayingFullDialogue) {
                                    handleSentencePlayClick(sentenceIndex);
                                    }
                                  }}
                                  disabled={isPlayingFullDialogue}
                                  title={isPlayingFullDialogue ? '播放全文中，请等待播放完成' : '播放这句话'}
                                >
                                  <svg 
                                    viewBox="0 0 24 24" 
                                    className="speaker-icon"
                                    fill="none"
                                  >
                                    <path 
                                      d="M11 5L6 9H2v6h4l5 4V5z" 
                                      fill="currentColor"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinejoin="round"
                                    />
                                    <path 
                                      d="M15.54 8.46a5 5 0 0 1 0 7.07" 
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                    />
                                    <path 
                                      d="M18.36 5.64a9 9 0 0 1 0 12.73" 
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                    />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
        
        {/* 第二部分 - 控制按钮区 */}
        <div className="role-play-section-2">
          <div className="role-play-controls">
            {/* 左侧控制按钮区 */}
            <div className="rp-controls-left">
              <button 
                className="rp-control-btn rp-prev-sentence-btn"
                onClick={previousSentence}
                disabled={currentDialogueIndex === 0}
                title="上一篇"
              >
                上一篇
              </button>
              
              <button 
                className="rp-control-btn rp-next-sentence-btn"
                onClick={nextSentence}
                disabled={currentDialogueIndex === dialogues.length - 1}
                title="下一篇"
              >
                下一篇
              </button>
            </div>
            
            {/* 中间功能按钮区 - 听和读 */}
            <div className="rp-controls-center">
              <button 
                className="rp-icon-btn rp-listen-btn"
                onClick={playFullDialogue}
                disabled={isPlayingFullDialogue || !checkAllRoleRecordingsComplete()}
                title={checkAllRoleRecordingsComplete() ? "播放整篇对话" : "请先完成所有扮演角色的录音"}
              >
                <img src={listenIcon} alt="听" />
              </button>
              
              <button 
                className={`rp-icon-btn rp-speak-btn ${isRecording ? 'recording' : ''} ${waitingForUserSpeak ? 'waiting' : ''} ${isEvaluating ? 'evaluating' : ''}`}
                onClick={handleUserSpeak}
                disabled={isPlayingFullDialogue || isEvaluating || (isPlaying && !isRecording)}
                title={
                  isPlayingFullDialogue ? '播放全文中，请等待播放完成' :
                  isEvaluating ? '评测中...' :
                  (isPlaying && !isRecording) ? '音频播放中，请等待播放完成' :
                  isRecording ? '点击停止录音' : 
                  (waitingForUserSpeak ? '请点击开始说话' : '跟读当前句子')
                }
              >
                <img src={speakIcon} alt="读" />
                {isRecording && (
                  <div className="recording-indicator">
                    <span className="recording-dot"></span>
                    <span className="recording-time">{recordingDuration}s</span>
                  </div>
                )}
                {isEvaluating && (
                  <div className="evaluating-indicator">
                    <span className="evaluating-spinner"></span>
                    <span className="evaluating-text">评测中...</span>
                  </div>
                )}
                {waitingForUserSpeak && !isRecording && !isEvaluating && <span className="waiting-indicator">点击开始录音</span>}
              </button>
            </div>
            
            {/* 右侧功能按钮区 */}
            <div className="rp-controls-right">
              <button 
                className="rp-control-btn rp-back-btn"
                onClick={handleBack}
                title="返回课文选择页面"
              >
                返回同步课文
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* 右侧区域 - 已选对话列表和评测结果 */}
      <div className="role-play-right">
        <div className="role-play-dialogues-list">
          <div className="dialogues-list-header">
            <h3>已选对话 ({selectedDialogues.length})</h3>
          </div>
          <div className="dialogues-list-content">
            {selectedDialogues.map((dialogue, index) => {
              const isCurrentDialogue = dialogues.length > 0 && 
                dialogues[currentDialogueIndex] && 
                dialogues[currentDialogueIndex].articleId === dialogue.article_id;
              
              return (
                <div 
                  key={dialogue.article_id} 
                  className={`dialogue-list-item ${isCurrentDialogue ? 'active' : ''} ${isPlayingFullDialogue ? 'disabled' : ''}`}
                  onClick={() => !isPlayingFullDialogue && switchToDialogue(dialogue.article_id)}
                  style={{ cursor: isPlayingFullDialogue ? 'not-allowed' : 'pointer' }}
                >
                  <div className="dialogue-list-item-title">{dialogue.title}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* 领读设置弹窗 */}
      <SettingsModal
        isOpen={showReadingSettings}
        onClose={handleReadingSettingsClose}
        onStart={handleReadingSettingsUpdate}
        title={articleReadingConfig.title}
        settingItems={articleReadingConfig.settingItems}
        defaultSettings={readingSettings}
      />
      
    </div>
    
    {/* 评测结果图片显示 */}
    {showScoreImage && (
      <div className="score-image-overlay">
        <div className="score-image-container">
          <img 
            src={
              scoreImageType === '测评好' ? scoreGoodImage :
              scoreImageType === '测评差' ? scoreBadImage :
              scoreImageType === '测评中' ? scoringImage : ''
            } 
            alt={scoreImageType}
            className="score-image"
          />
        </div>
      </div>
    )}
    
    {/* 提示框 - 放在 container 外面，避免被 overflow:hidden 影响 */}
    <Alert
      show={showAlert}
      message={alertMessage}
      onHide={() => setShowAlert(false)}
      type="warning"
      duration={1200}
    />
    </>
  );
};

export default RolePlay;

